import { ref } from "#imports";
import { COOKIES_NAMES } from "~/utils/constants.js";
import { useClientCookie } from "~/composables/useClientCookie.js";
import { destroySwiperInstance as _destroySwiperInstance } from "~/utils/destroySwiperInstance";

export const useHeroSwiper = () => {
  const { getCookie, setCookie } = useClientCookie(
    COOKIES_NAMES.weekMoviesActiveIndex,
  );

  const swiperInstance = ref(null);
  const progressBarMobile = ref(null);
  const progressBarDesktop = ref(null);
  const activeIndex = ref(null);
  const isHydrated = ref(false);

  const swiperProgressBar = reactive({
    slideTime: 10000,
    progressValue: 1,
  });

  /**
   * Initialize active index from cookie (client-side only)
   */
  const initializeActiveIndexFromCookie = () => {
    if (import.meta.server || isHydrated.value) return;
    if (typeof document === "undefined") return;

    isHydrated.value = true;

    const stored = getCookie();
    if (stored !== null) {
      const parsedIndex = parseInt(stored, 10);
      if (!isNaN(parsedIndex)) {
        activeIndex.value = parsedIndex;
      }
    }
  };

  /**
   * Set active index and persist to cookie
   * @param {number|null} index - Active index or null to clear
   */
  const setActiveIndex = (index) => {
    activeIndex.value = index;
    if (import.meta.client) {
      if (index !== null && index !== undefined) {
        setCookie(String(index));
      }
    }
  };

  // Auto-initialize on client
  if (import.meta.client) {
    initializeActiveIndexFromCookie();
  }

  const initSwiperInstance = (instance) => {
    swiperInstance.value = instance;
  };

  const destroySwiperInstance = () => _destroySwiperInstance(swiperInstance);

  const onAutoplayTimeLeft = (s, time, progress) => {
    swiperProgressBar.progressValue = (1 - progress) * 100;
    const activeProgressBarMobile =
      progressBarMobile.value?.[swiperInstance.value?.realIndex ?? 0] ?? null;
    if (activeProgressBarMobile) {
      activeProgressBarMobile.style.height =
        swiperProgressBar.progressValue + "%";
    }
    const activeProgressBarDesktop =
      progressBarDesktop.value?.[swiperInstance.value?.realIndex ?? 0] ?? null;
    if (activeProgressBarDesktop) {
      activeProgressBarDesktop.style.width =
        swiperProgressBar.progressValue + "%";
    }
  };

  const resetProgressBar = () => {
    if (progressBarMobile.value?.length > 0) {
      progressBarMobile.value.forEach((element) => {
        element.style.height = 0;
      });
    }
    if (progressBarDesktop.value?.length > 0) {
      progressBarDesktop.value.forEach((element) => {
        element.style.width = 0;
      });
    }
  };

  const slideTo = (index) => {
    if (
      !swiperInstance.value?.slides?.length ||
      swiperInstance.value?.realIndex === index
    )
      return;
    swiperInstance.value.slideTo(index);
  };

  return {
    swiperInstance,
    progressBarMobile,
    progressBarDesktop,
    swiperProgressBar,
    activeIndex: computed(() => activeIndex.value),
    initSwiperInstance,
    destroySwiperInstance,
    setActiveIndex,
    onAutoplayTimeLeft,
    resetProgressBar,
    slideTo,
  };
};
